import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChatListHeader } from '../ChatListHeader';
import * as S from './styles';
import { useMessage } from 'newComponents/ChatWhatsapp/context/MessageContext';
import { LoadingMessageContainer, MessageTimestamp } from '../ChatArea/styles';
import { ChatListItem } from './ChatListItem';

const ChatList = ( {
	conversations,
	searchQuery,
	showUnreadOnly,
	setSearchQuery,
	activeTab
} ) => {
	const { loadChats, chatListCursor, oficialChatListCursor, loadMoreOficialFinalized, isLoadingChats, unreadActiveChats } = useMessage();
	const chatListRef = useRef( null );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ showEndedConversations, setShowEndedConversations ] = useState( false );
	const [ activeFilter, setActiveFilter ] = useState( 'all' );

	const { activeConversations, endedConversations, favoriteConversations } = React.useMemo( () => {
		if ( !conversations ) return { activeConversations: [], endedConversations: [], pendingConversations: [], favoriteConversations: [] };

		const active = [];
		const ended = [];
		const pending = [];
		const favorite = [];

		conversations.forEach( conversation => {
			if ( conversation?.status === 'Finalizado' ) {
				ended.push( conversation );
			} else if ( conversation?.isFavorite && conversation?.status === 'Pendente' ) {
				pending.push( conversation );
				favorite.push( conversation );
				active.push( conversation );
			} else if ( conversation?.isFavorite && conversation?.status !== 'Pendente' ) {
				favorite.push( conversation );
				active.push( conversation );
			} else if ( conversation?.status === 'Pendente' ) {
				pending.push( conversation );
				active.push( conversation );
			} else {
				active.push( conversation );
			}
		} );

		const sortByDate = ( a, b ) => {
			const dateA = new Date( a?.createdAt || 0 );
			const dateB = new Date( b?.createdAt || 0 );
			return dateB - dateA;
		};

		active.sort( sortByDate );
		ended.sort( sortByDate );
		pending.sort( sortByDate );
		favorite.sort( sortByDate );

		return { activeConversations: active, endedConversations: ended, pendingConversations: pending, favoriteConversations: favorite };
	}, [ conversations ] );

	const normalizedQuery = searchQuery.trim().toLowerCase();
	// Busca só com dígitos e pontuação (ex.: nº do processo sem máscara) também compara os
	// dígitos do processo: "00012345620238260100" acha "0001234-56.2023.8.26.0100" (AFCO-545).
	const queryDigits = /^[\d.\-/\s]+$/.test( normalizedQuery ) ? normalizedQuery.replace( /\D/g, '' ) : '';

	const filterConversations = ( conversations ) => {
		return conversations?.filter( conversation => {
			const processNumber = conversation?.processNumber;
			// Só os campos preenchidos: antes o texto juntava "null"/"undefined" e o separador "||",
			// e buscar por eles trazia chats que não tinham nada a ver.
			const fields = [ conversation?.name, conversation?.id, conversation?.caseId, processNumber, conversation?.content ]
				.filter( Boolean )
				.map( value => String( value ).toLowerCase() );
			const matchesSearch = !normalizedQuery
				|| fields.some( field => field.includes( normalizedQuery ) )
				|| ( queryDigits.length > 0 && !!processNumber && String( processNumber ).replace( /\D/g, '' ).includes( queryDigits ) );

			const matchesFilter =
				activeFilter === 'all'
					? true
					: activeFilter === 'Favorita' ? conversation?.isFavorite : activeFilter === 'Pendente'
						? conversation?.status === 'Pendente' || conversation?.unreadCount > 0
						: conversation?.status === activeFilter;

			if ( showUnreadOnly ) {
				return matchesSearch && conversation?.unreadCount > 0 && matchesFilter;
			}
			return matchesSearch && matchesFilter;
		} );
	};

	const filteredActiveConversations = filterConversations( activeConversations );
	const filteredEndedConversations = filterConversations( endedConversations );

	const handleLoadChatList = async () => {
		if ( !chatListRef.current ) return;
		setIsLoading( true );

		const chatDiv = chatListRef.current;

		await loadChats();
		if ( oficialChatListCursor ) {
			await loadMoreOficialFinalized();
		}

		setIsLoading( false );

		const isAtBottom =
      chatDiv.scrollHeight - chatDiv.clientHeight <= chatDiv.scrollTop + 10;

		if ( isAtBottom ) {
			chatDiv.scrollTop = chatDiv.scrollHeight;
		}
	};

	const handleFilterChange = ( filter ) => {
		if ( filter === 'Finalizado' ) {
			setShowEndedConversations( true );
			setActiveFilter( 'all' );
		} else if ( filter === 'all' ) {
			setShowEndedConversations( false );
			setActiveFilter( 'all' );
		} else if ( filter === 'Favorita' ) {
			setActiveFilter( 'Favorita' );
			setShowEndedConversations( false );
		} else {
			setActiveFilter( filter );
			setShowEndedConversations( false );
		}
	};

	return (
		<S.ChatListContainer ref={chatListRef}>
			<ChatListHeader
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
				activeTab={activeTab}
			/>

			{activeTab !== 'pending' && (
				<S.FilterContainer>
					<S.FilterButton
						active={activeFilter === 'all' && !showEndedConversations}
						onClick={() => handleFilterChange( 'all' )}
						title="Todas as conversas"
					>
						<span className="filter-text">Conversas ({activeConversations.length})</span>
					</S.FilterButton>
					<S.FilterButton
						active={activeFilter === 'Pendente' && !showEndedConversations}
						onClick={() => handleFilterChange( 'Pendente' )}
						title="Conversas pendentes"
					>
						<span className="filter-text">Não lidas ({unreadActiveChats})</span>
					</S.FilterButton>
					<S.FilterButton
						active={activeFilter === 'Favorita' && !showEndedConversations}
						onClick={() => handleFilterChange( 'Favorita' )}
						title="Conversas favoritas"
					>
						<span className="filter-text">Favoritas ({favoriteConversations.length})</span>
					</S.FilterButton>
					<S.FilterButton
						active={showEndedConversations}
						onClick={() => handleFilterChange( 'Finalizado' )}
						title="Conversas finalizadas"
					>
						<span className="filter-text">Finalizadas</span>
					</S.FilterButton>
				</S.FilterContainer>
			)}

			{isLoadingChats && (
				<LoadingMessageContainer>
					<MessageTimestamp>Carregando conversas...</MessageTimestamp>
				</LoadingMessageContainer>
			)}

			{showEndedConversations ? (
				<div style={{ width: '100%' }}>
					{filteredEndedConversations?.map( ( conv ) => <ChatListItem key={conv?.id} conversation={conv} /> )}
					{filteredEndedConversations?.length === 0 && (
						<div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
							{normalizedQuery ? 'Nenhum resultado encontrado' : 'Nenhuma conversa finalizada'}
						</div>
					)}
				</div>
			) : (
				<div style={{ width: '100%' }}>
					{filteredActiveConversations?.map( ( conv ) => <ChatListItem key={conv?.id} conversation={conv} /> )}
					{filteredActiveConversations?.length === 0 && (
						<div style={{ textAlign: 'center', padding: '20px', color: '#171414' }}>
							{normalizedQuery ? 'Nenhum resultado encontrado' : 'Para iniciar uma conversa, visite a tela do caso e selecione um número'}
						</div>
					)}
				</div>
			)}

			{!isLoading && ( chatListCursor || oficialChatListCursor ) && activeTab !== 'pending' && showEndedConversations && (
				<S.LoadMoreButton type='button' onClick={handleLoadChatList}>
					Carregar Mais
				</S.LoadMoreButton>
			)}
			{isLoading && (
				<LoadingMessageContainer>
					<MessageTimestamp>Carregando mensagens...</MessageTimestamp>
				</LoadingMessageContainer>
			)}
		</S.ChatListContainer>
	);
};

ChatList.propTypes = {
	conversations: PropTypes.arrayOf(
		PropTypes.shape( {
			id          : PropTypes.string.isRequired,
			name        : PropTypes.string,
			content     : PropTypes.string,
			createdAt   : PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.instanceOf( Date ) ] ),
			unreadCount : PropTypes.number,
			contact     : PropTypes.shape( {
				profilePicUrl: PropTypes.string
			} ),
			profilePicUrl : PropTypes.string,
			closedAt      : PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.instanceOf( Date ) ] ),
			status        : PropTypes.string
		} )
	),
	searchQuery     : PropTypes.string.isRequired,
	setSearchQuery  : PropTypes.func.isRequired,
	handleChatClick : PropTypes.func.isRequired,
	activeTab       : PropTypes.string
};

export { ChatList };
